/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import { LayerType } from '@opengeoweb/webmap';
import {
  emptyGeoJSON,
  defaultIntersectionStyleProperties,
  getLastEmptyFeatureIndex,
  addSelectionTypeToGeoJSON,
  getFeatureCollection,
  getGeoJson,
  moveFeature,
} from '@opengeoweb/webmap-react';
import type { DrawtoolModuleStore } from './reducer';
import { drawingToolActions } from './reducer';
import { selectDrawToolById, getDrawModeById } from './selectors';
import type { WebMapStateModuleState } from '../map';
import { layerSelectors, layerActions } from '../map';
import { getFeatureSelectionKey } from '../map/layer/utils';

export const drawingToolListener = createListenerMiddleware<
  DrawtoolModuleStore & WebMapStateModuleState
>();
const registerOrigin = 'drawings listener:registerDrawToolListener';

const getSelectedFeatureIds = (feature?: GeoJSON.Feature): string[] => {
  const selectedFeatureId = getFeatureSelectionKey(feature);
  return selectedFeatureId ? [selectedFeatureId] : [];
};

// register draw tool
drawingToolListener.startListening({
  actionCreator: drawingToolActions.registerDrawTool,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const {
      mapId,
      geoJSONLayerId,
      geoJSONIntersectionLayerId,
      geoJSONIntersectionBoundsLayerId,
      defaultGeoJSON = emptyGeoJSON,
      defaultGeoJSONIntersection = emptyGeoJSON,
      defaultGeoJSONIntersectionBounds,
      defaultGeoJSONIntersectionProperties = defaultIntersectionStyleProperties,
    } = payload;

    // create for every drawTool a draw layer
    if (geoJSONLayerId && mapId) {
      listenerApi.dispatch(
        layerActions.addLayer({
          mapId,
          layer: {
            // empty geoJSON
            geojson: defaultGeoJSON,
            layerType: LayerType.featureLayer,
          },
          layerId: geoJSONLayerId,
          origin: registerOrigin,
        }),
      );
    }

    // create intersection layer
    if (geoJSONIntersectionLayerId && mapId) {
      listenerApi.dispatch(
        layerActions.addLayer({
          mapId,
          layer: {
            geojson: defaultGeoJSONIntersection,
            layerType: LayerType.featureLayer,
            defaultGeoJSONProperties: defaultGeoJSONIntersectionProperties,
          },
          layerId: geoJSONIntersectionLayerId,
          origin: registerOrigin,
        }),
      );
    }
    // create intersection bounds layer
    if (geoJSONIntersectionBoundsLayerId && mapId) {
      const existingBoundsLayer = layerSelectors.getLayerById(
        listenerApi.getState(),
        geoJSONIntersectionBoundsLayerId,
      );

      // don't add a new boundslayer when another drawtool using that same boundslayer
      if (!existingBoundsLayer) {
        listenerApi.dispatch(
          layerActions.addLayer({
            mapId,
            layer: {
              geojson: defaultGeoJSONIntersectionBounds,
              layerType: LayerType.featureLayer,
            },
            layerId: geoJSONIntersectionBoundsLayerId,
            origin: registerOrigin,
          }),
        );
      }

      listenerApi.dispatch(
        layerActions.orderLayerToFront({
          layerId: geoJSONIntersectionBoundsLayerId,
        }),
      );
    }
  },
});

// change draw tool
drawingToolListener.startListening({
  actionCreator: drawingToolActions.changeDrawToolMode,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    try {
      const { drawModeId, drawToolId, shouldUpdateShape = true } = payload;
      const drawingTool = selectDrawToolById(
        listenerApi.getState(),
        drawToolId,
      );

      if (!drawingTool) {
        return;
      }

      const {
        shouldAllowMultipleShapes = false,
        geoJSONIntersectionLayerId,
        geoJSONIntersectionBoundsLayerId,
      } = drawingTool;

      const newDrawMode = getDrawModeById(
        listenerApi.getState(),
        drawToolId,
        drawModeId,
      );

      const geoJSONLayer = layerSelectors.getLayerById(
        listenerApi.getState(),
        drawingTool.geoJSONLayerId,
      );
      const geoJSON = geoJSONLayer?.geojson;

      // disable layer when no new drawmode or when selecting same tool and is selectable
      if (
        !newDrawMode ||
        (!drawingTool.activeDrawModeId && newDrawMode.isSelectable)
      ) {
        listenerApi.dispatch(
          layerActions.toggleFeatureMode({
            layerId: drawingTool.geoJSONLayerId,
            isInEditMode: false,
            drawMode: '',
          }),
        );

        const lastFeatureIndex =
          geoJSON !== undefined ? getLastEmptyFeatureIndex(geoJSON) : undefined;

        if (lastFeatureIndex !== undefined) {
          const newGeoJSON = {
            ...geoJSON,
            features: (geoJSON as GeoJSON.FeatureCollection).features.filter(
              (_feature, index) => index !== lastFeatureIndex,
            ),
          } as GeoJSON.FeatureCollection;

          listenerApi.dispatch(
            layerActions.updateFeature({
              layerId: drawingTool.geoJSONLayerId,
              geojson: newGeoJSON,
            }),
          );

          const selectedFeature =
            newGeoJSON.features.length > 0
              ? newGeoJSON.features[newGeoJSON.features.length - 1]
              : undefined;

          listenerApi.dispatch(
            layerActions.setSelectedFeatures({
              layerId: drawingTool.geoJSONLayerId,
              selectedFeatureIds: getSelectedFeatureIds(selectedFeature),
            }),
          );
        }

        return;
      }

      // delete selected shape when multiple allowed
      if (newDrawMode.value === 'DELETE' && shouldAllowMultipleShapes) {
        const selectedFeatureIndex = layerSelectors.getActiveFeatureIndex(
          listenerApi.getState(),
          drawingTool.geoJSONLayerId,
        );
        const isLayerInEditMode = layerSelectors.getIsLayerInEditMode(
          listenerApi.getState(),
          drawingTool.geoJSONLayerId,
        );

        const selectedFeature =
          selectedFeatureIndex !== undefined && geoJSON
            ? (geoJSON as GeoJSON.FeatureCollection).features[
                selectedFeatureIndex
              ]
            : undefined;
        const selectedFeatureId = getFeatureSelectionKey(selectedFeature);

        if (isLayerInEditMode && selectedFeatureId) {
          listenerApi.dispatch(
            layerActions.deleteFeature({
              layerId: drawingTool.geoJSONLayerId,
              featureId: selectedFeatureId,
            }),
          );
        } else {
          // if nothing selected, delete all
          listenerApi.dispatch(
            layerActions.layerChangeGeojson({
              layerId: drawingTool.geoJSONLayerId,
              geojson: emptyGeoJSON,
            }),
          );
        }

        listenerApi.dispatch(
          layerActions.toggleFeatureMode({
            layerId: drawingTool.geoJSONLayerId,
            isInEditMode: false,
            drawMode: '',
          }),
        );

        return;
      }

      // delete shape
      if (newDrawMode.value === 'DELETE') {
        listenerApi.dispatch(
          layerActions.layerChangeGeojson({
            layerId: drawingTool.geoJSONLayerId,
            geojson: emptyGeoJSON,
          }),
        );

        // clear intersection shape
        if (geoJSONIntersectionLayerId) {
          listenerApi.dispatch(
            layerActions.layerChangeGeojson({
              layerId: geoJSONIntersectionLayerId,
              geojson: emptyGeoJSON,
            }),
          );
        }

        listenerApi.dispatch(
          layerActions.toggleFeatureMode({
            layerId: drawingTool.geoJSONLayerId,
            isInEditMode: false,
            drawMode: '',
          }),
        );

        return;
      }

      // check tool is selected of existing drawn shape
      const currentGeoJSONLayer = layerSelectors.getLayerById(
        listenerApi.getState(),
        drawingTool.geoJSONLayerId,
      );

      const { geojson: currentGeoJSON } = currentGeoJSONLayer || {};
      const selectedFeatureIndex =
        layerSelectors.getActiveFeatureIndex(
          listenerApi.getState(),
          drawingTool.geoJSONLayerId,
        ) ?? 0;

      const currentSelectionType =
        currentGeoJSON?.features[selectedFeatureIndex]?.properties
          ?.selectionType;
      const newSelectionType = newDrawMode.selectionType;

      const isNewToolSelected = currentSelectionType !== newSelectionType;

      // don't change anything if same tool is selected again
      // but for shouldAllowMultipleShapes same tool will also be a new drawing
      const shouldUpdateNewShape =
        !currentGeoJSON?.features?.length ||
        isNewToolSelected ||
        shouldAllowMultipleShapes;

      if (shouldUpdateNewShape && shouldUpdateShape) {
        const shapeWithSelectionType = addSelectionTypeToGeoJSON(
          newDrawMode.shape,
          newDrawMode.selectionType,
        );
        const geoJSONFeatureCollection = getFeatureCollection(
          shapeWithSelectionType,
          shouldAllowMultipleShapes,
          currentGeoJSON,
        );
        const newGeoJSON = getGeoJson(
          geoJSONFeatureCollection,
          shouldAllowMultipleShapes,
        );

        if (shouldAllowMultipleShapes) {
          moveFeature(currentGeoJSON!, newGeoJSON, selectedFeatureIndex, '');
        }

        const selectedFeature = shouldAllowMultipleShapes
          ? ({
              ...newGeoJSON.features[newGeoJSON.features.length - 1],
              // Keep this in sync with layerReducer.updateFeature, which
              // materializes multi-shape feature ids from their array index.
              id: newGeoJSON.features.length - 1,
            } as GeoJSON.Feature)
          : newGeoJSON.features[newGeoJSON.features.length - 1];

        listenerApi.dispatch(
          layerActions.updateFeature({
            layerId: drawingTool.geoJSONLayerId,
            geojson: newGeoJSON,
            ...(geoJSONIntersectionLayerId && {
              geoJSONIntersectionLayerId,
            }),
            ...(geoJSONIntersectionBoundsLayerId && {
              geoJSONIntersectionBoundsLayerId,
            }),
          }),
        );

        listenerApi.dispatch(
          layerActions.setSelectedFeatures({
            layerId: drawingTool.geoJSONLayerId,
            selectedFeatureIds: getSelectedFeatureIds(selectedFeature),
          }),
        );
      }

      listenerApi.dispatch(
        layerActions.toggleFeatureMode({
          layerId: drawingTool.geoJSONLayerId,
          isInEditMode: newDrawMode.isSelectable,
          drawMode: newDrawMode.value,
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error changeDrawToolListener', error);
    }
  },
});

// change intersection
drawingToolListener.startListening({
  actionCreator: drawingToolActions.changeIntersectionBounds,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { drawToolId, geoJSON } = payload;

    const drawtool = selectDrawToolById(listenerApi.getState(), drawToolId);

    if (!drawtool) {
      return;
    }

    listenerApi.dispatch(
      layerActions.layerChangeGeojson({
        layerId: drawtool.geoJSONIntersectionBoundsLayerId!,
        geojson: geoJSON,
      }),
    );
    listenerApi.dispatch(
      layerActions.layerChangeGeojson({
        layerId: drawtool.geoJSONLayerId,
        geojson: emptyGeoJSON,
      }),
    );
    listenerApi.dispatch(
      layerActions.layerChangeGeojson({
        layerId: drawtool.geoJSONIntersectionLayerId!,
        geojson: emptyGeoJSON,
      }),
    );
  },
});
